import { csrfHeaders, ensureCsrfToken } from "./client";

/**
 * SSE client for the IMP AI chat.
 *
 * Streams from `POST /api/assistant/chat` (text/event-stream). We mirror the
 * conventions of `api/client.ts`: same `/api` base URL and `credentials:"include"`
 * so the httpOnly auth cookies travel with the request, plus the double-submit
 * CSRF header that every state-changing call now carries. The API key never
 * reaches the frontend: the backend proxies Anthropic.
 *
 * The backend emits named frames: `token` | `tool` | `cards` | `done` | `error`.
 */

const API_BASE = "/api";

export type Card = { title: string; subtitle?: string; href: string };

export type ChatHandlers = {
  onToken: (t: string) => void;
  onTool: (name: string, status: string) => void;
  onCards: (c: Card[]) => void;
  onDone: () => void;
  onError: (code: string, msg: string) => void;
};

export type ChatMessageInput = { role: string; content: string };

/**
 * Pure SSE frame extractor. Splits a raw buffer into complete `event:`/`data:`
 * frames (separated by a blank line) and returns the trailing incomplete frame
 * as `rest` so the caller can prepend it to the next chunk.
 */
export function parseSse(buffer: string): { events: { event: string; data: string }[]; rest: string } {
  const events: { event: string; data: string }[] = [];
  // Normalise CRLF so the split logic only reasons about "\n".
  const normalised = buffer.replace(/\r\n/g, "\n");

  const lastSep = normalised.lastIndexOf("\n\n");
  if (lastSep === -1) return { events, rest: normalised };

  const complete = normalised.slice(0, lastSep);
  const rest = normalised.slice(lastSep + 2);

  for (const frame of complete.split("\n\n")) {
    if (!frame.trim()) continue;
    let event = "message";
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
    }
    events.push({ event, data: dataLines.join("\n") });
  }
  return { events, rest };
}

function dispatch(ev: { event: string; data: string }, h: ChatHandlers): void {
  switch (ev.event) {
    case "token":
      try {
        h.onToken(JSON.parse(ev.data).text ?? "");
      } catch {
        /* ignore malformed frame */
      }
      break;
    case "tool":
      try {
        const d = JSON.parse(ev.data);
        h.onTool(d.name, d.status);
      } catch {
        /* ignore */
      }
      break;
    case "cards":
      try {
        h.onCards(JSON.parse(ev.data).cards ?? []);
      } catch {
        /* ignore */
      }
      break;
    case "done":
      h.onDone();
      break;
    case "error":
      try {
        const d = JSON.parse(ev.data);
        h.onError(d.code ?? "error", d.message ?? "");
      } catch {
        h.onError("error", "");
      }
      break;
    default:
      /* unknown event name — ignore */
      break;
  }
}

/**
 * POST the conversation and stream the assistant's reply, dispatching each SSE
 * frame to the matching handler. Resolves when the stream closes.
 */
export async function streamChat(
  messages: ChatMessageInput[],
  locale: string,
  h: ChatHandlers,
  signal?: AbortSignal,
): Promise<void> {
  // This client bypasses apiFetch (SSE needs the raw body reader), so it must repeat apiFetch's
  // CSRF lesson itself: no cookie yet -> warm it first, or the POST goes out headerless and dies
  // as a 403 that Spring never logs. Headers are BUILT PER ATTEMPT for the same reason apiFetch
  // builds init per attempt — a retry must not resend the pre-warm, header-less request.
  await ensureCsrfToken();
  const send = () => fetch(`${API_BASE}/assistant/chat`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...csrfHeaders(),
    },
    body: JSON.stringify({ messages, locale }),
    signal,
  });

  let res: Response;
  try {
    res = await send();
    if (res.status === 403) {
      // One re-warm and one retry: the cookie can be stale after a session refresh. A second 403
      // is a real refusal and falls through to the error handler below.
      await ensureCsrfToken();
      res = await send();
    }
  } catch (e) {
    if ((e as Error)?.name === "AbortError") return;
    h.onError("network", (e as Error)?.message ?? "network error");
    return;
  }

  if (res.status === 429) {
    h.onError("rate_limited", "rate limited");
    return;
  }
  if (!res.ok || !res.body) {
    h.onError(`http_${res.status}`, res.statusText || "request failed");
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = parseSse(buffer);
      buffer = rest;
      for (const ev of events) dispatch(ev, h);
    }
    // Flush a trailing frame that arrived without a final blank line.
    buffer += decoder.decode();
    if (buffer.trim()) {
      const { events } = parseSse(`${buffer}\n\n`);
      for (const ev of events) dispatch(ev, h);
    }
  } catch (e) {
    if ((e as Error)?.name === "AbortError") return;
    h.onError("stream", (e as Error)?.message ?? "stream error");
  }
}
